/**
 * Вкладка «Атрибуты» — всё, что человек правит у макета, в одном месте.
 *
 * Собрана из бывших «Атрибутов» и «Контента и ИИ»: описание изображения и текст
 * на нём — такие же правимые поля, ради них не нужна отдельная вкладка. Убраны
 * «Системные данные» отдельной карточкой (три readonly-поля занимали экран) —
 * они свёрнуты внизу. «Статус макета» исчез: состояние теперь производное,
 * человек ставит только ручной флаг «приостановлен / архив».
 */

import React, { useState } from 'react'
import { ChevronDown, Sparkles } from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { Button } from '../../../../components/ui/Button'
import { cn } from '../../../../lib/cn'
import {
  LIFECYCLE_FLAG_OPTIONS,
  SEO_STATUSES,
  designStateLabel,
  designStateVariant,
  designTitle,
  formatDateTime,
} from '../../lib'
import type { DesignV2 } from '../../types'
import { Fact, SEC, SelectField, Field, TextAreaField } from './parts'

const LANGUAGE_OPTIONS = [
  'русский',
  'английский',
  'французский',
  'немецкий',
  'испанский',
  'итальянский',
  'японский',
  'китайский',
  'украинский',
  'белорусский',
  'казахский',
  'узбекский',
  'армянский',
  'грузинский',
  'азербайджанский',
  'молдавский',
  'литовский',
  'латышский',
  'эстонский',
  'киргизский',
  'таджикский',
  'туркменский',
  'универсальное',
]

const AGE_UNITS = ['мес', 'год', 'года', 'лет']

interface AttributesTabProps {
  design: DesignV2
  form: Record<string, string>
  setForm: React.Dispatch<React.SetStateAction<Record<string, string>>>
  dirtyFields: Set<string>
  dictionaries: Record<string, string[]>
  onGenerateAi: () => void
}

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <section className="rounded-lg border border-gray-200 dark:border-zinc-800">
    <header className="border-b border-gray-200 px-4 py-2.5 dark:border-zinc-800">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</h3>
      {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">{hint}</p>}
    </header>
    <div className="p-4">{children}</div>
  </section>
)

const ChipList: React.FC<{ label: string; values: string[]; tone: 'blue' | 'purple' }> = ({
  label,
  values,
  tone,
}) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="text-xs text-gray-500 dark:text-zinc-400">{label}:</span>
    {values.map((value) => (
      <span
        key={value}
        className={cn(
          'rounded px-1.5 py-px text-2xs font-medium',
          tone === 'blue'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
            : 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
        )}
      >
        {value}
      </span>
    ))}
  </div>
)

export const AttributesTab: React.FC<AttributesTabProps> = ({
  design,
  form,
  setForm,
  dirtyFields,
  dictionaries,
  onGenerateAi,
}) => {
  const [systemOpen, setSystemOpen] = useState(false)
  const set = (field: string) => (value: string) => setForm((f) => ({ ...f, [field]: value }))
  const seo = SEO_STATUSES.find((s) => s.value === (design.seo_status || 'empty'))

  return (
    <div className="space-y-4">
      <Section title="Классификация" hint="По этим полям макет находят в каталоге и фильтрах">
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
          <SelectField
            label="Тип макета"
            value={form.type}
            options={dictionaries.type || []}
            changed={dirtyFields.has('type')}
            onChange={set('type')}
          />
          <SelectField
            label="Категория"
            value={form.category}
            options={dictionaries.category || []}
            changed={dirtyFields.has('category')}
            onChange={set('category')}
          />
          <SelectField
            label="Бренд"
            value={form.brand_ru}
            options={dictionaries.brand || []}
            changed={dirtyFields.has('brand_ru')}
            onChange={set('brand_ru')}
          />
        </div>
      </Section>

      <Section title="Кому и по какому поводу" hint="Аудитория макета — она же основа для SEO">
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
          <SelectField
            label="Пол аудитории"
            value={form.gender}
            options={dictionaries.gender || []}
            changed={dirtyFields.has('gender')}
            onChange={set('gender')}
          />
          <SelectField
            label="Возраст аудитории"
            value={form.age}
            options={dictionaries.age || []}
            changed={dirtyFields.has('age')}
            onChange={set('age')}
          />
          <SelectField
            label="Повод"
            value={form.occasion}
            options={dictionaries.occasion || []}
            changed={dirtyFields.has('occasion')}
            onChange={set('occasion')}
          />
        </div>
        {((design.personas || []).length > 0 || (design.recipient_statuses || []).length > 0) && (
          <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-3 dark:border-zinc-800">
            {(design.personas || []).length > 0 && (
              <ChipList label="Персоны" values={design.personas!} tone="blue" />
            )}
            {(design.recipient_statuses || []).length > 0 && (
              <ChipList label="Кому дарят" values={design.recipient_statuses!} tone="purple" />
            )}
          </div>
        )}
      </Section>

      <Section title="Что на макете" hint="Отсюда ИИ берёт материал для названий и описаний карточек">
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field
            label="Имя на макете"
            value={form.name_on_image}
            changed={dirtyFields.has('name_on_image')}
            onChange={set('name_on_image')}
            placeholder="если есть"
          />
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Возраст"
              type="number"
              value={form.age_on_image}
              changed={dirtyFields.has('age_on_image')}
              onChange={set('age_on_image')}
              placeholder="5"
            />
            <SelectField
              label="Единица"
              value={form.age_on_image_unit}
              options={AGE_UNITS}
              changed={dirtyFields.has('age_on_image_unit')}
              onChange={set('age_on_image_unit')}
              emptyLabel="—"
            />
          </div>
          <SelectField
            label="Язык надписи"
            value={form.language}
            options={LANGUAGE_OPTIONS}
            changed={dirtyFields.has('language')}
            onChange={set('language')}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <TextAreaField
            label="Что изображено"
            value={form.image_subject}
            changed={dirtyFields.has('image_subject')}
            onChange={set('image_subject')}
            placeholder="Например: Щенячий патруль, Гонщик и Скай на фоне города"
          />
          <TextAreaField
            label="Текст на изображении"
            value={form.image_text}
            changed={dirtyFields.has('image_text')}
            onChange={set('image_text')}
            placeholder="Надпись, которая напечатана на макете"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50 px-3.5 py-3 dark:border-indigo-900 dark:bg-indigo-950/40">
          <Sparkles className="h-5 w-5 shrink-0 text-indigo-600 dark:text-indigo-400" />
          <div className="min-w-0 flex-1 text-[0.8125rem] text-gray-700 dark:text-zinc-300">
            <b className="block text-sm text-gray-900 dark:text-zinc-100">
              Заполнить по изображению
            </b>
            ИИ распознает картинку и предложит значения. Ничего не сохранится без вашего
            подтверждения.
          </div>
          <div className="flex items-center gap-2">
            {seo && <Badge variant={seo.value === 'approved' ? 'green' : 'gray'}>SEO: {seo.label}</Badge>}
            <Button size="sm" onClick={onGenerateAi}>
              Запустить ИИ
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title="Доступность макета"
        hint="Состояние считается автоматически; вручную можно только приостановить или сдать в архив"
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div>
            <div className="mb-1.5 text-xs font-medium text-gray-700 dark:text-zinc-300">
              Сейчас
            </div>
            <div className="flex min-h-10 items-center">
              <Badge variant={designStateVariant(design.design_state)}>
                {designStateLabel(design.design_state)}
              </Badge>
            </div>
          </div>
          <SelectField
            label="Ручной флаг"
            value={form.lifecycle_flag}
            options={LIFECYCLE_FLAG_OPTIONS.filter((o) => o.value).map((o) => o.value)}
            optionLabel={(value) =>
              LIFECYCLE_FLAG_OPTIONS.find((o) => o.value === value)?.label ?? value
            }
            changed={dirtyFields.has('lifecycle_flag')}
            onChange={set('lifecycle_flag')}
            emptyLabel="Автоматически"
          />
        </div>
      </Section>

      <div className="rounded-lg border border-gray-200 dark:border-zinc-800">
        <button
          type="button"
          onClick={() => setSystemOpen((open) => !open)}
          aria-expanded={systemOpen}
          className="flex w-full items-center gap-2 px-4 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-gray-400 transition-transform',
              systemOpen && 'rotate-180',
            )}
          />
          <span className={SEC}>Сведения о записи</span>
          <span className="ml-auto text-2xs text-gray-400 dark:text-zinc-500">
            наименование, правовой статус, даты
          </span>
        </button>
        {systemOpen && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-gray-200 p-4 sm:grid-cols-3 dark:border-zinc-800">
            <Fact label="Наименование">{design.design_name || designTitle(design)}</Fact>
            <Fact label="Правовой статус">{design.legal_status || '—'}</Fact>
            <Fact label="Сезонность">{design.seasonal ? 'Сезонный' : 'Несезонный'}</Fact>
            <Fact label="Создан">{formatDateTime(design.created_at)}</Fact>
            <Fact label="Принят">{formatDateTime(design.accepted_at)}</Fact>
            <Fact label="Изменён">{formatDateTime(design.updated_at)}</Fact>
          </div>
        )}
      </div>
    </div>
  )
}

export default AttributesTab
